import { locales, localeToLanguage } from '@worldcoo/shared/core';
import type { Locale, Language } from '@worldcoo/shared/core';
import type { TranslatableText } from '@worldcoo/monolith/core';

const localeSet: ReadonlySet<string> = new Set(locales);

/** Type guard that checks whether a string is a valid Locale */
export function isLocale(value: string): value is Locale {
	return localeSet.has(value);
}

/**
 * Resolve the current i18n locale to a `Locale` value.
 * Falls back to `'es'` when the locale string is not a known locale.
 */
export function getLocale(i18n: { locale: string }): Locale {
	return isLocale(i18n.locale) ? i18n.locale : 'es';
}

/**
 * Resolve the current i18n locale to the corresponding `Language` code.
 * Falls back to the Spanish language code when the locale string is not known.
 */
export function getLanguage(i18n: { locale: string }): Language {
	return localeToLanguage[getLocale(i18n)];
}

// ── Translatable record utilities ──────────────────────────────────────────

/** Marker key added to every Record<string, string> that represents translatable text */
const TRANSLATABLE = 'translatable';

/** Create a translatable record (adds the marker so `collectTranslatableEntries` can find it) */
export function translatable(record: Record<string, string> = {}): Record<string, string> {
	return { [TRANSLATABLE]: '', ...record };
}

/** Remove the marker key from a translatable record before sending to the API */
export function stripTranslatable(record: Record<string, string>): Record<string, string> {
	const { [TRANSLATABLE]: _, ...rest } = record;
	return rest;
}

/**
 * Ensure every configured language key exists with at least `''` as default.
 * Also adds the translatable marker so the record is discoverable.
 */
export function fillTranslatable(record: TranslatableText | undefined, languages: string[]): Record<string, string> {
	const filled: Record<string, string> = { [TRANSLATABLE]: '' };
	for (const lang of languages) filled[lang] = '';
	if (record) for (const [key, value] of Object.entries(record)) filled[key] = value;
	return filled;
}

// ── Translatable entry collection & translation ────────────────────────────

export type TranslatableEntry = { path: string; record: Record<string, string> };

/**
 * Recursively walk a values object and collect every record that carries the
 * `translatable` marker.  Returns each record together with its dot/bracket
 * path (e.g. `"name"`, `"donationAmounts[0].equivalent"`, `"otherImages[1].alt"`).
 */
export function collectTranslatableEntries(values: Record<string, unknown>, prefix = ''): TranslatableEntry[] {
	const entries: TranslatableEntry[] = [];
	for (const [key, val] of Object.entries(values)) {
		if (val === null || val === undefined || typeof val !== 'object' || val instanceof File) continue;
		const path = prefix ? `${prefix}.${key}` : key;
		if (TRANSLATABLE in val) {
			entries.push({ path, record: val as Record<string, string> });
		} else if (Array.isArray(val)) {
			for (let i = 0; i < val.length; i++) {
				const item = val[i];
				if (item === null || typeof item !== 'object') continue;
				if (TRANSLATABLE in item) {
					entries.push({ path: `${path}[${i}]`, record: item as Record<string, string> });
				} else if (!Array.isArray(item)) {
					entries.push(...collectTranslatableEntries(item as Record<string, unknown>, `${path}[${i}]`));
				}
			}
		}
	}
	return entries;
}

export interface FillTranslationsResult<T> {
	/** New copy of the input values with translations filled in */
	values: T;
	/** Updated AI-translation tracking metadata */
	aiTranslated: Record<string, string[]>;
	/** Languages that received translations (empty when nothing was needed) */
	translatedLanguages: string[];
	/** `true` when the AI translation API succeeded, `false` when source texts were copied as fallback */
	success: boolean;
}

/**
 * Return a new copy of `values` with missing translations filled in for every
 * translatable record (detected via the `translatable` marker).
 *
 * Nothing is mutated — the original `values` are left untouched.
 *
 * When no languages need translation the original `values` reference is returned
 * as-is (no copy).
 */
export async function autoTranslateMissingTranslations<
	T extends Record<string, unknown>,
	L extends string = string,
>(options: {
	values: T;
	defaultLanguage: L;
	languages: L[];
	/** Call the translation API — return the result or throw on failure */
	translate: (input: { sourceLanguage: L; targetLanguages: L[]; texts: Record<string, string> }) => Promise<unknown>;
}): Promise<FillTranslationsResult<T>> {
	const { values, defaultLanguage, languages, translate } = options;
	const currentAi = (values as Record<string, unknown>).aiTranslated as Record<string, string[]> | undefined;

	const entries = collectTranslatableEntries(values);
	const textsToTranslate = entries.filter((e) => e.record[defaultLanguage]);
	const langsNeedingTexts = languages.filter((l) => {
		if (l === defaultLanguage) return false;
		return textsToTranslate.some((e) => !e.record[l]);
	});

	if (langsNeedingTexts.length === 0 || textsToTranslate.length === 0) {
		return { values, aiTranslated: currentAi ?? {}, translatedLanguages: [], success: true };
	}

	// Call translation API
	const texts = Object.fromEntries(textsToTranslate.map((e) => [e.path, e.record[defaultLanguage]!]));
	let translated: Record<string, Record<string, string>> | null = null;
	try {
		const result = await translate({ sourceLanguage: defaultLanguage, targetLanguages: langsNeedingTexts, texts });
		if (result && typeof result === 'object' && !('error' in result)) {
			translated = result as Record<string, Record<string, string>>;
		} else {
			console.error('Translation failed:', result);
		}
	} catch (error) {
		console.error('Translation failed:', error);
	}

	// Build updated copies of each translated record and track AI metadata
	const updates = new Map<Record<string, string>, Record<string, string>>();
	const updatedAi = { ...currentAi };
	for (const { path, record } of textsToTranslate) {
		const updated = { ...record };
		let changed = false;
		for (const langCode of langsNeedingTexts) {
			if (updated[langCode]) continue;
			updated[langCode] = translated?.[langCode]?.[path] ?? record[defaultLanguage]!;
			changed = true;
			const aiLangs = updatedAi[path] ?? [];
			if (!aiLangs.includes(langCode)) updatedAi[path] = [...aiLangs, langCode];
		}
		if (changed) updates.set(record, updated);
	}

	// Build a new values tree replacing only the translated records
	const newValues = replaceRecords(values, updates) as T;
	(newValues as Record<string, unknown>).aiTranslated = updatedAi;

	return {
		values: newValues,
		aiTranslated: updatedAi,
		translatedLanguages: langsNeedingTexts,
		success: translated !== null,
	};
}

/** Shallow-copy an object tree, swapping any record found in `updates` with its replacement. */
function replaceRecords(
	values: Record<string, unknown>,
	updates: Map<Record<string, string>, Record<string, string>>,
): Record<string, unknown> {
	const result: Record<string, unknown> = {};
	for (const [key, val] of Object.entries(values)) {
		if (val !== null && typeof val === 'object' && !Array.isArray(val) && !(val instanceof File)) {
			const replacement = updates.get(val as Record<string, string>);
			result[key] = replacement ?? val;
		} else if (Array.isArray(val)) {
			result[key] = val.map((item) => {
				if (item === null || typeof item !== 'object' || item instanceof File) return item;
				const replacement = updates.get(item as Record<string, string>);
				if (replacement) return replacement;
				if (!Array.isArray(item)) return replaceRecords(item as Record<string, unknown>, updates);
				return item;
			});
		} else {
			result[key] = val;
		}
	}
	return result;
}
